import dayjs from "dayjs";
import { getOccurrenceWindow } from "%/utils/event-occurrence-window";
import { isLegacyEditorEvent, legacyEditorRecurrenceFields } from "%/dynamic-component-editor/helpers/event-rrule";
import { defineStore } from "pinia";
import { useEventFeedStore } from "SmartFeed/stores/feed/event-feed-store";
import { useEventTempFeedStore } from "~/smart-feed/stores/feed/event-temp-feed-store";
import {
  getBrowserTimezone,
  createDefaultEvent,
  getStartTimestamp,
  getEndTimestamp,
  getEarlyJoinMinutes,
  getEventRRule,
  getRecurringEndTimestamp,
  isRecurringEvent,
  readEventRecurrence,
} from "%/utils/event-helper";
import {
  EVENT_ACTIVE_STATUS,
  EVENT_INACTIVE_STATUS,
  SAVE_SCOPE_THIS_OCCURRENCE,
  SAVE_SCOPE_WHOLE_SERIES,
} from "%/constants/event-constants";

const snakeCaseWindow = window => ({
  start_timestamp: window.startTimestamp,
  end_timestamp: window.endTimestamp,
});

export const useEventStore = defineStore("event-store-v2", {
  state: () => ({
    addEventError: null,
    loadEventError: "",
    isUploadingImage: false,
    isLoading: false,
    isLoadingEventContext: false,
    currentEvent: createDefaultEvent(),
    showAddEventModal: false,
    editEventId: null,
    pendingSaveScope: null,
    showSaveScopeModal: false,
  }),

  getters: {
    saveScopeSummary(state) {
      const occurrenceStart = Number(state.currentEvent?.occurrenceStart) || 0;

      if (!occurrenceStart) {
        return "";
      }

      return `You are editing the ${dayjs.unix(occurrenceStart).format("D MMM YYYY")} occurrence.`;
    },
  },

  actions: {
    setEvent(event) {
      this.currentEvent = {
        ...createDefaultEvent(),
        ...event,
        recurrenceRule: this.readRecurrence(event),
        hashtags: (event.hashtags || []).map(hashtag => ({
          hashtagId: hashtag.hashtagId,
          hashtag: hashtag.hashtag || hashtag.value || "",
        })),
      };
      this.editEventId = Number(event?.eventId) || null;
      this.addEventError = null;
      this.loadEventError = "";
    },

    readRecurrence(event) {
      try {
        return readEventRecurrence(event);
      } catch (error) {
        this.loadEventError = `This event's repeat schedule could not be read: ${error.message}`;

        // eslint-disable-next-line no-console
        console.error(
          "[events] this event carries a repeat rule the editor cannot show, so the form would "
            + "open as if it never repeated and erase the rule on save",
          { eventId: event && event.eventId, rrule: event && event.rrule, error },
        );

        return null;
      }
    },

    buildRecurrence(event, startTimestamp) {
      if (isLegacyEditorEvent(event)) {
        return { ...legacyEditorRecurrenceFields(event), ok: true };
      }

      try {
        const rrule = getEventRRule(event);

        return {
          rrule,
          recurringStartTimestamp: isRecurringEvent(event) ? startTimestamp : 0,
          recurringEndTimestamp: getRecurringEndTimestamp(event),
          ok: true,
        };
      } catch (error) {
        this.addEventError = error.message;
        this.isLoading = false;

        return { rrule: "", ok: false };
      }
    },

    prepareCreateEvent() {
      this.currentEvent = createDefaultEvent();
      this.editEventId = null;
      this.addEventError = null;
      this.loadEventError = "";
    },

    resetEvent() {
      this.prepareCreateEvent();
    },

    getEventContext(eventId, timestamp = null) {
      this.isLoadingEventContext = true;
      this.loadEventError = "";

      const query = timestamp ? `?timestamp=${timestamp}` : "";

      return this.apiGet(
        `/event/${eventId}${query}`,
        {},
        data => {
          this.setEvent(data);
        },
        response => {
          this.loadEventError =
            response?.response?.data?.data?.message ||
            "Could not load the event. Please try again.";
        },
        () => {
          this.isLoadingEventContext = false;
        },
      );
    },

    openCreateEventModal() {
      this.prepareCreateEvent();
      this.showAddEventModal = true;
    },

    openEditEventModal(eventId, event) {
      this.setEvent({
        ...event,
        eventId,
      });
      this.showAddEventModal = true;
    },

    closeEventModal() {
      this.showAddEventModal = false;
      this.prepareCreateEvent();
    },

    uploadImage(image, publicationId, onSuccess) {
      this.isUploadingImage = true;

      const formData = new FormData();
      formData.append("image", image);
      formData.append("collection_id", publicationId);

      this.apiPost(
        "/event/upload-image",
        formData,
        data => {
          const imageUrl = data.image;

          if (this.currentEvent) {
            this.currentEvent = {
              ...this.currentEvent,
              imageUrl,
            };
          }

          if (typeof onSuccess === "function") {
            onSuccess(imageUrl);
          }
        },
        () => {
          this.addEventError = "Failed to upload image, please retry.";
        },
        () => {
          this.isUploadingImage = false;
        },
      );
    },
    createEvent(publicationId, event = this.currentEvent) {
      if (!event) {
        throw new Error("createEvent called without an event object");
      }

      this.isLoading = true;
      let createdEventId = null;

      let ticketPrice = 0;

      if (event.allowTicketPurchase) {
        ticketPrice = Number(event.ticketPrice);
      }

      const startTimestamp = getStartTimestamp(event);
      const endTimestamp = getEndTimestamp(event);
      const eventTimezoneId = getBrowserTimezone();

      const recurrence = this.buildRecurrence(event, startTimestamp);

      if (!recurrence.ok) {
        return null;
      }

      const rrule = recurrence.rrule;

      const hashtagValues = event.hashtags.map(hashtag => hashtag.hashtag);

      return this.apiPost(
        "/event/create",
        {
          title: event.title,
          description: event.description,
          event_timestamp: Math.round(event.eventTimestamp),
          image_url: event.imageUrl,
          meeting_link: event.meetingLink,
          location: event.location,
          event_type: event.eventType,
          allow_ticket_purchase: event.allowTicketPurchase,
          allow_early_join: event.allowEarlyJoin,
          early_join_minutes: getEarlyJoinMinutes(event),
          hide_chat_outside_meeting: event.hideChatOutsideMeeting,
          ticket_price: ticketPrice,
          publication_id: publicationId,
          status: event.status || EVENT_ACTIVE_STATUS,
          recurring_start_timestamp: recurrence.recurringStartTimestamp,
          recurring_end_timestamp: recurrence.recurringEndTimestamp,
          start_timestamp: startTimestamp,
          end_timestamp: endTimestamp,
          event_timezone: eventTimezoneId,
          rrule,
          is_all_day: Boolean(event.isAllDay),
          hashtags: hashtagValues,
        },
        data => {
          createdEventId = Number(data.event_id) || null;
          this.editEventId = createdEventId;
          this.currentEvent = {
            ...createDefaultEvent(),
            ...event,
            eventId: createdEventId,
          };
          this.addEventError = null;
          this.loadEventError = "";

          useEventFeedStore().reload();
          useEventTempFeedStore().reload();

          this.resetEvent();
          this.showAddEventModal = false;
        },
        response => {
          this.addEventError =
                response?.response?.data?.data?.message ||
                "Failed to create event, please retry.";
        },
        () => {
          this.isLoading = false;
        },
      ).then(() => createdEventId);
    },
    saveEvent(publicationId) {
      if (!this.needsSaveScope()) {
        return this.updateEvent(publicationId);
      }

      this.pendingSaveScope = { publicationId };
      this.showSaveScopeModal = true;

      return null;
    },

    needsSaveScope() {
      return Boolean(
        this.currentEvent
          && this.currentEvent.recurrenceRule
          && Number(this.currentEvent.occurrenceStart) > 0
          && (Number(this.currentEvent.eventId) || this.editEventId),
      );
    },

    cancelSaveScope() {
      this.pendingSaveScope = null;
      this.showSaveScopeModal = false;
    },

    confirmSaveScope(scope) {
      const pending = this.pendingSaveScope;

      this.showSaveScopeModal = false;
      this.pendingSaveScope = null;

      if (!pending) {
        return null;
      }

      if (scope === SAVE_SCOPE_WHOLE_SERIES) {
        return this.updateEvent(pending.publicationId);
      }

      if (scope === SAVE_SCOPE_THIS_OCCURRENCE) {
        return this.updateOneOccurrence();
      }

      return this.splitSeriesAndUpdate(pending.publicationId);
    },

    updateOneOccurrence() {
      const eventId = Number(this.currentEvent.eventId) || this.editEventId;
      const occurrenceStart = Number(this.currentEvent.occurrenceStart);

      this.isLoading = true;

      return this.apiPost(
        `/event/${eventId}/occurrence/save`,
        {
          occurrence: occurrenceStart,
          ...snakeCaseWindow(getOccurrenceWindow(this.currentEvent)),
          title: this.currentEvent.title,
          description: this.currentEvent.description,
        },
        () => {
          this.addEventError = null;
          useEventFeedStore().reload();
          useEventTempFeedStore().reload();
          this.resetEvent();
          this.showAddEventModal = false;
        },
        response => {
          this.addEventError =
            response?.response?.data?.data?.message
            || "Failed to save this occurrence, please retry.";
        },
        () => {
          this.isLoading = false;
        },
      );
    },

    splitSeriesAndUpdate(publicationId) {
      const eventId = Number(this.currentEvent.eventId) || this.editEventId;
      const occurrenceStart = Number(this.currentEvent.occurrenceStart);

      this.isLoading = true;

      return this.apiPost(
        `/event/${eventId}/occurrence/split`,
        { occurrence: occurrenceStart },
        data => {
          const tailEventId = Number(data.tail_event_id) || eventId;

          this.currentEvent = { ...this.currentEvent, eventId: tailEventId };
          this.editEventId = tailEventId;
          this.isLoading = false;
          this.updateEvent(publicationId);
        },
        response => {
          this.isLoading = false;
          this.addEventError =
            response?.response?.data?.data?.message
            || "Failed to split this series, please retry.";
        },
      );
    },

    cancelOneOccurrence(eventId, occurrenceStart) {
      return this.apiPost(
        `/event/${eventId}/occurrence/cancel`,
        { occurrence: occurrenceStart },
        () => {
          useEventFeedStore().reload();
          useEventTempFeedStore().reload();
        },
        response => {
          this.addEventError =
            response?.response?.data?.data?.message
            || "Failed to cancel this occurrence, please retry.";
        },
      );
    },

    updateEvent(
      publicationId,
    ) {
      const currentEventId = Number(this.currentEvent.eventId) || this.editEventId;

      if (!currentEventId) {
        throw new Error("updateEvent called without an eventId");
      }

      this.isLoading = true;

      let ticketPrice = 0;

      if (this.currentEvent.allowTicketPurchase) {
        ticketPrice = Number(this.currentEvent.ticketPrice);
      }

      const startTimestamp = getStartTimestamp(this.currentEvent);
      const endTimestamp = getEndTimestamp(this.currentEvent);
      const eventTimezoneId = getBrowserTimezone();
      const hashtagValues = this.currentEvent.hashtags.map(hashtag => hashtag.hashtag);

      const recurrence = this.buildRecurrence(this.currentEvent, startTimestamp);

      if (!recurrence.ok) {
        return null;
      }

      const rrule = recurrence.rrule;

      return this.apiPost(
        `/event/${currentEventId}/update`,
        {
          title: this.currentEvent.title,
          description: this.currentEvent.description,
          event_timestamp: Math.round(this.currentEvent.eventTimestamp),
          image_url: this.currentEvent.imageUrl,
          meeting_link: this.currentEvent.meetingLink,
          location: this.currentEvent.location,
          event_type: this.currentEvent.eventType,
          allow_ticket_purchase: this.currentEvent.allowTicketPurchase,
          allow_early_join: this.currentEvent.allowEarlyJoin,
          early_join_minutes: getEarlyJoinMinutes(this.currentEvent),
          hide_chat_outside_meeting: this.currentEvent.hideChatOutsideMeeting,
          ticket_price: ticketPrice,
          publication_id: publicationId,
          metadata: "",
          status: this.currentEvent.status || EVENT_ACTIVE_STATUS,
          recurring_start_timestamp: recurrence.recurringStartTimestamp,
          recurring_end_timestamp: recurrence.recurringEndTimestamp,
          start_timestamp: startTimestamp,
          end_timestamp: endTimestamp,
          event_timezone: eventTimezoneId,
          rrule,
          is_all_day: Boolean(this.currentEvent.isAllDay),
          hashtags: hashtagValues,
        },
        data => {
          useEventFeedStore().smartFeedEngine.addEntities(
            data.event.entities,
          );
          useEventTempFeedStore().smartFeedEngine.addEntities(
            data.event_temp.entities,
          );

          this.editEventId = currentEventId;
          this.addEventError = null;
          this.loadEventError = "";

          this.showAddEventModal = false;
        },
        response => {
          this.addEventError =
                response?.response?.data?.data?.message ||
                "Failed to update event, please retry.";
        },
        () => {
          this.isLoading = false;
        },
      ).then(() => currentEventId);
    },
    removeHashtagFromEvent(hashtagId, eventId, onError = () => {}) {
      this.apiPost(
        `/event/${eventId}/untag`,
        { hashtag_id: hashtagId },
        data => {
          useEventFeedStore().smartFeedEngine.addEntities(data.event.entities);
          useEventTempFeedStore().smartFeedEngine.addEntities(data.event_temp.entities);
        },
        onError,
      );
    },

    changeStatus(event, eventId, publicationId = this.publicationId) {
      if (!event) {
        throw new Error("changeStatus called without an event object");
      }

      if (!eventId) {
        throw new Error("changeStatus called without an eventId");
      }

      const newStatus =
          event.status === EVENT_ACTIVE_STATUS
            ? EVENT_INACTIVE_STATUS
            : EVENT_ACTIVE_STATUS;

      const updatedEvent = {
        ...event,
        status: newStatus,
        eventId,
      };

      this.setEvent(updatedEvent);
      this.updateEvent(publicationId);
    },
  },
});
